// Higher-order component (HOC) for session management and authentication
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import routes from '@/app/routes';

import useRouterQuery from '@/utils/routerQuery';
import Loader from '@/components/Loader';

import {
  beginSession,
  signIn,
  setIsAuthorized,
  validateUser,
  signOut,
  setResponseErrorStatus,
  updateUser,
} from '../store/sessionSlice';

import {
  selectIsAppInitialized,
  selectSessionLoading,
  selectSessionUser,
  selectUserAuthorized,
} from '../store/session.selector';
import type { User } from '../types/session.types';
import { selectGlobalError } from '@/app/selector';
import { updateConfig } from '@/app/coreSlice';
import appLocalStorage from '../store/appLocalStorage';

// withSession HOC wraps a component and injects session/auth logic
const withSession =
  (WrappedComponent: React.ComponentType<any>) => (props: any) => {
    const query = useRouterQuery();
    const navigate = useNavigate();
    const loading: boolean = useAppSelector(selectSessionLoading);
    const user: User | null = useAppSelector(selectSessionUser);
    const isAuthorized: boolean = useAppSelector(selectUserAuthorized);
    const globalError = useAppSelector(selectGlobalError);
    const isAppInitialized = useAppSelector(selectIsAppInitialized);
    const dispatch = useAppDispatch();

    useEffect(() => {
      dispatch(beginSession(query.get('code')))
        .unwrap()
        .then(response => {
          if (response?.user) {
            dispatch(validateUser())
              .unwrap()
              .then(userData => {
                const selectedProductionUnit =
                  appLocalStorage.getProductionUnit();
                const userPlants = userData?.plants || [];
                // Check if selected production unit in localStorage is still valid for the user, if not select the first available plant or set to empty if no plants are available
                let defaultProductionUnit = selectedProductionUnit || '';
                if (!userPlants.includes(selectedProductionUnit)) {
                  defaultProductionUnit =
                    userPlants.length > 0 ? userPlants[0] : '';
                }
                dispatch(
                  updateConfig({
                    productionUnit: defaultProductionUnit,
                  })
                );
                dispatch(updateUser({ plants: userPlants }));
                dispatch(setIsAuthorized());
                if (
                  (!selectedProductionUnit ||
                    defaultProductionUnit !== selectedProductionUnit) &&
                  userPlants.length > 0
                ) {
                  appLocalStorage.setProductionUnit(userPlants[0]);
                }
              })
              .catch(error => {
                dispatch(setResponseErrorStatus(error || 401));
              });
          } else {
            dispatch(signOut());
          }
        })
        .catch(error => {
          console.error('Error initializing session:', error);
          dispatch(signOut());
        });
    }, []);

    useEffect(() => {
      // Navigate to error page if global error is set
      if (globalError) {
        navigate(routes.ERROR);
      }
    }, [globalError]);

    // Handler for signing out user
    const signOutHandler = () => {
      dispatch(signOut());
    };

    // Handler for signing in user
    const signInHandler = () => {
      dispatch(signIn());
    };

    // Show loader while app is initializing or session is loading
    if (!isAppInitialized || loading) {
      return <Loader />;
    }
    // Render wrapped component with injected session props
    return (
      <WrappedComponent
        isAuthorized={isAuthorized}
        user={user}
        signIn={signInHandler}
        signOut={signOutHandler}
        {...props}
      />
    );
  };

export default withSession;
